import moment from 'moment';
import { dmyToIso } from './vision360.utils';

// Calendario de cobros de una membresía (360 · Mascotas y planes): reparte su vigencia en
// períodos (12 meses o un solo año) y marca cuáles tienen pago. Solo se registran los cobros
// exitosos, así que el mes cubierto por cada pago es una estimación: el pago N (del más antiguo
// al más reciente) cubre el período N.

const ANNUAL = 'Anual';
const MONTHS_PER_YEAR = 12;
// Mercado Pago suele cobrar unos días después del inicio del período; antes de este plazo un
// período sin pago no se marca como vencido.
const GRACE_DAYS = 10;
const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function parseDate(text) {
  const iso = dmyToIso(text);
  return iso ? moment.utc(iso, 'YYYY-MM-DD', true) : null;
}

function periodStarts(start, end, annual) {
  if (annual) return [start.clone()];
  const starts = [];
  for (let i = 0; i < MONTHS_PER_YEAR; i += 1) {
    const periodStart = start.clone().add(i, 'months');
    if (end && !periodStart.isBefore(end)) break;
    starts.push(periodStart);
  }
  return starts;
}

function periodLabel(periodStart, annual) {
  if (annual) return `${periodStart.year()}`;
  return MONTH_NAMES[periodStart.month()].slice(0, 3);
}

export function coveredPeriodText(periodStart, annual) {
  if (annual) return `Año ${periodStart.format('DD/MM/YYYY')}`;
  return `${MONTH_NAMES[periodStart.month()]} ${periodStart.year()}`;
}

function unpaidStatus(periodStart, today, hasNext) {
  if (periodStart.clone().add(GRACE_DAYS, 'days').isBefore(today)) return 'missed';
  return hasNext ? 'future' : 'next';
}

/**
 * Arma el calendario de cobros de una membresía de membership-data.
 * @param {object} membership - con startdate, endDate / expirationDate (DD/MM/YY) y paymentInterval.
 * @param {Array} payments - cobros de la membresía, del más reciente al más antiguo (como los entrega el backend).
 * @param {Date} [now] - fecha de referencia.
 * @returns {{ annual: boolean, periods: Array, paidCount: number, nextCharge: string|null,
 *   coveredByPayment: Map }} períodos con status paid | missed | next | future; coveredByPayment
 *   asocia cada pago (por índice en `payments`) con el texto del período que cubre.
 */
export function buildPaymentCalendar(membership, payments = [], now = new Date()) {
  const empty = { annual: false, periods: [], paidCount: 0, nextCharge: null, coveredByPayment: new Map() };
  const start = membership && parseDate(membership.startdate);
  if (!start) return empty;

  const annual = membership.paymentInterval === ANNUAL;
  const end = parseDate(membership.endDate) || parseDate(membership.expirationDate);
  const today = moment.utc(now).startOf('day');
  const total = payments.length;
  const coveredByPayment = new Map();
  let hasNext = false;

  const periods = periodStarts(start, end, annual).map((periodStart, i) => {
    // Pago i en orden cronológico = índice (total - 1 - i) en la lista del backend.
    const paymentIndex = total - 1 - i;
    const label = periodLabel(periodStart, annual);
    if (paymentIndex >= 0) {
      coveredByPayment.set(paymentIndex, coveredPeriodText(periodStart, annual));
      return { key: periodStart.format('YYYY-MM'), label, status: 'paid', payment: payments[paymentIndex] };
    }
    const status = unpaidStatus(periodStart, today, hasNext);
    if (status === 'next') hasNext = true;
    return { key: periodStart.format('YYYY-MM'), label, status, date: periodStart.format('DD/MM/YYYY'), payment: null };
  });

  const next = periods.find((p) => p.status === 'next');
  return {
    annual,
    periods,
    paidCount: periods.filter((p) => p.status === 'paid').length,
    nextCharge: next ? next.date : null,
    coveredByPayment
  };
}
